import { CommonDialogs } from "@mcleod/common";
import { Label, Panel, Snackbar } from "@mcleod/components";
import { Api, DateUtil, DisplayType, getAuthSettings, getUnauthSettings, ModelRow } from "@mcleod/core";
import { LtlUtil } from "./ltl/LtlUtil";
import { FtlOrder } from "./FtlOrder";
import { LtlOrder } from "./ltl/LtlOrder";


const snackbarPops = { padding: 0, width: 450 };

interface Contact {
  name: string;
  phone: string;
  email: string;
  subject: string;
}

enum UserType {
  CUSTOMER = "C",
  LOCATION = "L",
}

export function userCanEnterFtlOrder(): boolean {
  return isPermissionsAllowed("InternetModuleOrderEntry") && isCustomerUser() ;
}

export async function userCanEnterLtlOrder(): Promise<boolean> {
  if (!LtlUtil.isAsset() || !isPermissionsAllowed("InternetModulePickupEntry"))
    return false;

  switch(getAuthSettings()?.user_settings?.user_type) {
    case UserType.CUSTOMER: return true;
    case UserType.LOCATION: {
      if (getCompanySettings()?.allow_location_order_entry) {
        return Api.search("portal/common/location", {get_customer_id: true}).then(result => {
          return result?.data?.[0]?.customer_id != null
        });
      }
      return false;
    }
    default: return false;
  }
}

export function userCanEnterLtlQuote(): boolean {
  return LtlUtil.isLtlEnabled() && isPermissionsAllowed("InternetModuleQuoteEntry") && isCustomerUser();
}

function isPermissionsAllowed(permName:string) :boolean {
  return getAuthSettings().permissions?.denied?.InternetModule?.[permName] == null;
}

function isCustomerUser(): boolean {
  return UserType.CUSTOMER == getAuthSettings()?.user_settings?.user_type;
}

function isLocationUser(): boolean {
  return UserType.LOCATION == getAuthSettings()?.user_settings?.user_type;
}

function getCompanySettings(): any {
  return getUnauthSettings().company_settings;
}

function getCustomerSettings(): any {
  return getAuthSettings()["customer_settings"] ?? {};
}

export function validateBeforeAddOrder(layout: FtlOrder | LtlOrder) {
    let disabledTooltip = null;
    if (!layout.activeRow.getBoolean("credit_status_valid", false)) {
        const creditStatus = layout.activeRow.get("credit_status") ?? "";
        switch (creditStatus) {
            case "": {
                disabledTooltip = "Your credit status is not on file";
                break;
            }
            case "T": {
                disabledTooltip = "Your credit status is tentative";
                break;
            }
            case "D": {
                disabledTooltip = "Your credit status is denied";
            }
        }
        showCreditSnackbar(layout.activeRow.getBoolean("credit_status_valid", null), true, layout, creditStatus);
    }
    if (validateRevenuCode(layout.activeRow.get("revenue_code_id"), true, layout) == false) {
        if (disabledTooltip)
            disabledTooltip += " and a revenue code is required"
        else
            disabledTooltip = "A revenue code is required";
    }

    if (disabledTooltip) {
        layout.buttonSubmitOrder.disabledTooltip = disabledTooltip + ". We're unable to accept your order.";
        layout.buttonSubmitOrder.enabled = false;
    }
}

export function validateLocationsPresent(): Promise<boolean> {
  if (getCompanySettings()?.enforce_shipper_id == true) {
    return Api.search("portal/common/location-suggestion", {
      text_search: "*",
      web_locations_only: true
    }).then(result => {
      const locationsPresent =  result?.data?.length > 0;
      if (!locationsPresent) {
        const locationErrorMessage = "Please contact " + getCompanySettings().order_contact_name + " at " + getCompanySettings().order_contact_phone + " to have your shippers added to your portal.";
        Snackbar.showWarningSnackbar({ ...snackbarPops, caption: locationErrorMessage}, { id: "noLocs", persist: true });
      }
      return locationsPresent;
    });
  } else {
    return Promise.resolve(true);
  }
}

function getContactInfo(name: string, phone: string, email: string, subject?: string):  Contact {
  const companySettings = getCompanySettings();
  return {
    name: name ? name : (companySettings.order_contact_name || "us"),
    phone: phone ? phone : companySettings.order_contact_phone,
    email: email ? email : companySettings.order_email_list,
    subject: subject
  };
}

function getContactSnackbarPanel(contact: Contact, prefix: string, suffix: string): Panel {
  const panel = new Panel({ ...snackbarPops});
  const labelProps = {rowBreak: false, paddingTop: 0,  paddingBottom: 0};
  const labelPhone = contact.phone ? new Label({...labelProps, caption: contact.phone, displayType: DisplayType.PHONE, fontBold: true, color:'#535d61'}) : null;
  const labelEmail = contact.email ?  new Label({...labelProps, caption: contact.email, displayType: DisplayType.EMAIL,fontBold: true, color:'#535d61'}) : null;
  if (labelEmail && contact.subject)
    labelEmail.link = `mailto:${contact.email}?subject=${contact.subject}`;

  if (labelPhone == null && labelEmail == null) {
    if (prefix)
      panel.add(new Label({...labelProps, rowBreak: true, caption: prefix}));
    return panel;
  }

  const lableMsg = new Label({...labelProps, rowBreak: true, caption: `Please contact ${contact.name}`});
  panel.add(lableMsg);
  if (prefix)
    lableMsg.caption = prefix + ` ${lableMsg.caption}`;
  lableMsg.caption = lableMsg.caption + " at ";

  if (labelEmail)
    panel.add(labelEmail);
  if (labelPhone) {
    if (labelEmail)
      panel.add(new Label({ ...labelProps, caption: "or"}));
    panel.add(labelPhone);
    panel.add(new Label({ ...labelProps, caption: suffix}));
  }
  return panel;
}

export function validateRevenuCode(revenueCode:any, isOrder:boolean, targetPanel?: Panel): boolean {
  const companySettings = getCompanySettings();
  if (companySettings?.enforce_revenue_code == true && revenueCode == null) {
    const customerSettings = getCustomerSettings();
    const assistanceContact = !companySettings.assistance_contact ? null
      : companySettings.assistance_contact === "S" ? customerSettings.salesperson : customerSettings.operations_user;


    let message = "A Revenue Code is required.  We can accept your quote request, but we cannot accept your order.";
    if (isOrder)
      message = "A Revenue Code is required. We're unable to accept your order.";

    const subject = `${getAuthSettings()?.user_settings?.user_name} with ${customerSettings.id} - ${customerSettings.name} needs an assigned revenue code`;
    const contact = getContactInfo(assistanceContact?.name, assistanceContact?.phone, assistanceContact?.email, subject);
    const panel = getContactSnackbarPanel(contact, message, null);
    Snackbar.showWarningSnackbar(panel, { id: "revCode", persist: true, targetPanel:targetPanel });
    return false;
  }
  return true;
}

export function validateCustomerCredit(isOrder: boolean, targetPanel?: Panel): boolean{
  const companySettings = getCompanySettings();
  const customerSettings = getCustomerSettings();
  const enforceCredit = companySettings["enforce_credit"];
  if (enforceCredit && (enforceCredit === "L" || enforceCredit === "S")) {
    const creditStatus = customerSettings && customerSettings.credit_status;
    return showCreditSnackbar(creditStatus && creditStatus === "D", isOrder, targetPanel, creditStatus);
  }
  else {
    return true;
  }
}

export function showCreditSnackbar(creditValid: boolean, isOrder: boolean, targetPanel?: Panel, creditStatus?: String): boolean {
    if (creditValid == false) {
        const collectionsContact = getCustomerSettings().collections_contact;
        const contact = getContactInfo(collectionsContact?.name, collectionsContact?.phone, collectionsContact?.email);

        let message;
        switch (creditStatus) {
            case "": {
                message = getCompanySettings().name + " does not have a credit status on file for your account.";
                break;
            }
            case "T": {
                message = "Your credit status is currently marked as tentative."
                break;
            }
            default: {
                message = "Your credit status is denied.  We can accept your quote request, but we cannot accept your order.";
                if (isOrder)
                    message = "Your credit status is denied. We're unable to accept your order.";
            }
        }
    
        if (message) {
            Snackbar.showWarningSnackbar(getContactSnackbarPanel(contact, message, "for more information."), { id: "creditCheck", persist: true, targetPanel: targetPanel });
        }
        return false;
    }
    else {
        return true;
    }
}

export function validateShipper(shipperLoc: string, quoteId: string, targetPanel?: Panel): boolean {
  const companySettings = getCompanySettings();
  if (companySettings?.enforce_shipper_id == true && shipperLoc == null) {
    const customerSettings = getCustomerSettings();
    const assistanceContact = !companySettings.assistance_contact ? null
      : companySettings.assistance_contact === "S" ? customerSettings.salesperson : customerSettings.operations_user;

    const subject = `${getAuthSettings()?.user_settings?.user_name} with ${customerSettings.id} - ${customerSettings.name} needs help converting quote #${quoteId} to an order`;
    const contact = getContactInfo(assistanceContact?.name, assistanceContact?.phone, assistanceContact?.email, subject);
    const panel = getContactSnackbarPanel(contact, null, "to have this shipper added to your account and to get assistance with completing this order.");
    Snackbar.showWarningSnackbar(panel, { id: "shipper", persist: true, targetPanel:targetPanel });
    return false;
  }
  return true;
}

export async function validatePrepTime(row: ModelRow<any>, showSnackbar: boolean = true): Promise<boolean> {
  const companySettings = getCompanySettings();
  const prepMinutes = companySettings.order_prep_time_minutes;
  const early = row?.get("sched_arrive_early");
  if (early == null || prepMinutes == 0) {
    return Promise.resolve(true);
  }
  const earlyDateTime = DateUtil.parseDateTime(early);
  const cityId = row.get("city_id");

  const earliestPossiblePickup = new Date();
  earliestPossiblePickup.setMinutes(earliestPossiblePickup.getMinutes() + prepMinutes);
  if (cityId != null) {
    return Api.post("portal/customer/dispatch/early-pickup-validator", { early_time: earlyDateTime, city_id: cityId }).then((isValidResponse) => {
      const isValid = isValidResponse.data[0].is_valid;
      if (isValid === false) {
        let message = "We need at least " + prepMinutes + " minutes lead time for a pickup. Please adjust your pickup time.";
        if (prepMinutes === 0)
          message = "Shipper's early pickup time must be later than shipper's present time. Please adjust your pickup time."
        if (showSnackbar)
          Snackbar.showWarningSnackbar({ caption: message, padding: 0, width: 450 }, { id: "prepTime", persist: false });
        return companySettings.is_asset_ltl ? false: true;
      }
      else {
        return true;
      }
    }).catch(reason => {
      CommonDialogs.showError("An error occurred while validating the earliest possible pickup", reason);
      return companySettings.is_asset_ltl ? false: true;
    });
  }
  else {
    return Promise.resolve(true);
  }
}

export function validatePickupWindow(row: ModelRow<any>, showSnackbar: boolean = true): boolean {
  const early = row?.get("sched_arrive_early");
  const late = row?.get("sched_arrive_late");
  if (early == null || late == null) {
    return true;
  }
  const earlyDateTime = DateUtil.parseDateTime(early);
  const lateDateTime = DateUtil.parseDateTime(late);
  const minWindowMinutes = getUnauthSettings().company_settings["order_pickup_window_minutes"];

  lateDateTime.setMinutes(lateDateTime.getMinutes() - minWindowMinutes);

  if (lateDateTime < earlyDateTime) {
    const message = "We need at least a " + minWindowMinutes + " minute pickup window.  Please adjust your Early Pickup and/or Late Pickup to allow for at least a " + minWindowMinutes + " minute window.";
    if (showSnackbar)
      Snackbar.showWarningSnackbar({ caption: message, padding: 0, width: 450 }, { id: "pickupWindow", persist: false });
    return false;
  }
  return true;
}
